import identity from "lodash/identity";
import map from "lodash/map";
import trim from "lodash/trim";

export default {
  methods: {
    printCss(suffix = "", convert = identity) {
      return (value, property) =>
        `--${property}${suffix ? "-" + suffix : ""}: ${convert(value)};`;
    },

    rgbToHsl(red, green, blue) {
      const r = Number(trim(red)) / 255;
      const g = Number(trim(green)) / 255;
      const b = Number(trim(blue)) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h,
        s,
        l = (max + min) / 2;

      if (max === min) {
        h = s = 0; // achromatic
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
        }
        h /= 6;
      }

      h = Math.round(360 * h);
      s = Math.round(100 * s);
      l = Math.round(100 * l);

      return [h, s, l];
    },

    colorToHsl(color) {
      if (color.slice(0, 1) === "#") {
        if (color.length === 4) {
          const r = parseInt(color.substr(1, 1) + color.substr(1, 1), 16);
          const g = parseInt(color.substr(2, 1) + color.substr(2, 1), 16);
          const b = parseInt(color.substr(3, 1) + color.substr(3, 1), 16);
          return this.rgbToHsl(r, g, b);
        } else {
          const r = parseInt(color.substr(1, 2), 16);
          const g = parseInt(color.substr(3, 2), 16);
          const b = parseInt(color.substr(5, 2), 16);
          return this.rgbToHsl(r, g, b);
        }
      } else if (color.slice(0, 4) === "rgba") {
        const [r, g, b] = color.slice(5, -1).split(",");
        return this.rgbToHsl(r, g, b).slice(0, 3);
      } else if (color.slice(0, 3) === "rgb") {
        const [r, g, b] = color.slice(4, -1).split(",");
        return this.rgbToHsl(r, g, b);
      } else if (color.slice(0, 4) === "hsla") {
        return color
          .slice(5, -1)
          .split(",")
          .slice(0, 3);
      } else if (color.slice(0, 3) === "hsl") {
        return color.slice(4, -1).split(",");
      } else {
        // named color values are not yet supported
        console.error(
          "Named color values are not supported in the config. Convert it manually using this chart: https://htmlcolorcodes.com/color-names/"
        );
        return [0, 0, 16]; // defaults to dark gray
      }
    },

    getHsl(colors) {
      return map(
        colors,
        this.printCss("", color => {
          const hsl = this.colorToHsl(color);
          return `hsl(${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%)`;
        })
      );
    },
    getH(colors) {
      return map(
        colors,
        this.printCss("h", color => {
          const hsl = this.colorToHsl(color);
          return hsl[0];
        })
      );
    },
    getS(colors) {
      return map(
        colors,
        this.printCss("s", color => {
          const hsl = this.colorToHsl(color);
          return `${hsl[1]}%`;
        })
      );
    },
    getL(colors) {
      return map(
        colors,
        this.printCss("l", color => {
          const hsl = this.colorToHsl(color);
          return `${hsl[2]}%`;
        })
      );
    },
    ApplyBranding({ colors }) {
      if (colors) {
        let style = `:root {`;
        const HSL = this.getHsl(colors);
        HSL.forEach(e => {
          style += e;
        });

        const H = this.getH(colors);
        H.forEach(e => {
          style += e;
        });

        const S = this.getS(colors);
        S.forEach(e => {
          style += e;
        });

        const L = this.getL(colors);
        L.forEach(e => {
          style += e;
        });

        style += `}`;
        return style;
      } else return null;
    },

    getStyleSheet() {
      let style = document.getElementById("branding");
      if (!style) {
        style = document.createElement("style");
        style.setAttribute("id", "branding");
      }
      return style;
    },

    getStyleSheetByTitle(title) {
      for (let index = 0; index < document.styleSheets.length; index++) {
        const sheet = document.styleSheets[index];
        if (sheet.ownerNode.getAttribute("id") === title) {
          return sheet;
        }
      }
    },

    setStyles(innerHTML) {
      const style = this.getStyleSheet();
      style.innerHTML = innerHTML;
      document.head.appendChild(style);
    },

    updateStyles(rule) {
      const style = this.getStyleSheetByTitle("branding");
      style.deleteRule(0);
      style.insertRule(rule, 0);
    },

    getFavicon() {
      const element = document.getElementById("favicon");
      return element;
    },

    setFavicon(href) {
      const element = this.getFavicon();
      element.href = href;
    }
  }
};
